import { AdyenCreditCardFormData } from '@hooks/useAdyenState.static';
import { BancontactAdyenCreditCardFormData } from '@hooks/useBancontactAdyenState.static';
import { getDefaultConfig } from '@utils/configUtils';

import { BaseRequestParameters, makePostWithParameters, axiosPost, axiosGet } from './api';
import { captureException } from './sentryService';

export type PSP = 'BRAINTREE' | 'ADYEN';
export type BaseCreditCardRequestParamsPM = 'CREDIT_CARD' | 'BANCONTACT';

export interface BaseCreditCardRequestParameters extends BaseRequestParameters {
  psp: PSP;
  paymentMethod: BaseCreditCardRequestParamsPM;
}

export interface SingleCreditCardRequestParameters extends BaseCreditCardRequestParameters {
  creditCardIdentifier: string;
  paymentMethod: BaseCreditCardRequestParamsPM;
}

interface BraintreeStoreCardData {
  braintreeNonce: string;
  braintreeLastFourNumber: string;
  braintreeCardholderName: string;
  braintreeExpirationMonth: string;
  braintreeExpirationYear: string;
  braintreeCardType: string;
  braintreeStoreCard: boolean;
  braintreeBinData: string;
  braintreeBin: string;
}

interface BraintreeStoreCardBody {
  braintreeZppInput: BraintreeStoreCardData;
}

interface AdyenStoreCardBody {
  adyenZppInput: AdyenCreditCardFormData;
}

interface BancontactAdyenStoreCardBody {
  bancontactZppInput: BancontactAdyenCreditCardFormData;
}

type StoreCardBody = BraintreeStoreCardBody | AdyenStoreCardBody | BancontactAdyenStoreCardBody;
export type StoreCardData =
  | BraintreeStoreCardData
  | AdyenCreditCardFormData
  | BancontactAdyenCreditCardFormData;

export const selectCreditCard = makePostWithParameters<SingleCreditCardRequestParameters, boolean>(
  '/checkout/editpayment/selectZppCreditCard',
);

export const deleteCreditCard = makePostWithParameters<
  SingleCreditCardRequestParameters,
  string | null
>('/checkout/editpayment/deleteZppCreditCard');

export const deleteAllCreditCards = makePostWithParameters<
  BaseCreditCardRequestParameters,
  boolean
>('/checkout/editpayment/deleteAllZppCreditCards');

/**
 * Store the Credit Card data (Adyen, Braintree) in a checkout session.
 *
 * @param params The request params
 * @param body Credit Card details body
 * @return The boolean value indicate if this operation is successful
 */
export const storeCreditCard = async (
  params: BaseCreditCardRequestParameters,
  body: StoreCardData,
): Promise<boolean> => {
  const { csrfToken, psp, paymentMethod } = params;
  const url = '/checkout/editpayment/storeZppCreditCardInSession';

  let storeCreditCardBody: StoreCardBody;

  if (paymentMethod === 'BANCONTACT') {
    storeCreditCardBody = {
      bancontactZppInput: body as BancontactAdyenCreditCardFormData,
    };
  } else {
    if (psp === 'ADYEN') {
      storeCreditCardBody = {
        adyenZppInput: body as AdyenCreditCardFormData,
      };
    } else {
      storeCreditCardBody = {
        braintreeZppInput: body as BraintreeStoreCardData,
      };
    }
  }

  try {
    const response = await axiosPost(url, csrfToken, storeCreditCardBody, params);
    return response.status === 200;
  } catch (error) {
    throw captureException({ error });
  }
};
/**
 * Fetches the favorite (saved) credit card details for a customer.
 *
 * @param customerId - Unique identifier of the customer.
 * @param siteId - Identifier of the site.
 * @param psp - Payment service provider ('BRAINTREE' or 'ADYEN').
 * @param keycloakToken -  keycloak authentication token.
 * @returns The details of the customer's favorite saved credit card.
 */
export const fetchFavoriteCreditCardNonce = async (
  customerId: string,
  siteId: string,
  psp: PSP,
  keycloakToken: string,
): Promise<string> => {
  const { zppApiBaseUrl } = getDefaultConfig();
  try {
    const headers = keycloakToken ? { Authorization: `Bearer ${keycloakToken}` } : undefined;

    const response = await axiosGet(
      `${zppApiBaseUrl}/braintree/credit-card-with-nonce?siteId=${siteId}`,
      undefined,
      headers,
    );
    return response.data.nonce;
  } catch (error) {
    throw captureException({ error });
  }
};
